/**
 * Progreso de planes de lectura — acceso LOCAL-FIRST a `bible_reading_progress`.
 *
 * Lectura: query reactiva PowerSync con TODAS las filas activas del usuario
 * (dataset diminuto: ≤ nº de días de los planes empezados). Escritura: marcar un
 * día completo inserta una fila (o restaura su tombstone); desmarcarlo aplica el
 * soft-delete (`deleted_at`), el MISMO contrato tombstone que `bible_bookmark`
 * — el write-path del servidor estampa `user_id` y verifica propiedad por fila.
 */
import { queryKeys } from "@bso/shared";
import { createPowerSyncQuery } from "@bso/shared/solid";
import { bible_reading_progress, eq } from "@bso/powersync";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import type { PlanProgressRow } from "../logic";

interface ProgressRow extends PlanProgressRow {
  id: string;
  deleted_at: string | null;
}

export function useReadingProgress() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();

  // TODAS las filas del usuario (incluidas tombstones, para poder restaurarlas
  // al re-marcar un día); la vista activa se filtra en memoria.
  const progressQuery = createPowerSyncQuery<ProgressRow>({
    queryKey: () => queryKeys.readingPlans.progress(session.userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const u = session.userId();
      if (!dbInstance || !u) {
        return null;
      }
      return dbInstance
        .select({
          id: bible_reading_progress.id,
          plan_id: bible_reading_progress.plan_id,
          day: bible_reading_progress.day,
          deleted_at: bible_reading_progress.deleted_at,
        })
        .from(bible_reading_progress)
        .where(eq(bible_reading_progress.user_id, u));
    },
    enabled: () => Boolean(db()) && Boolean(drizzle()) && Boolean(session.userId()),
    suspense: false,
  });

  /** Filas ACTIVAS (sin tombstone) — la entrada de `computePlanProgress`. */
  const activeRows = (): PlanProgressRow[] =>
    (progressQuery.data ?? []).filter((row) => row.deleted_at === null);

  const rowsOfDay = (planId: string, day: number): ProgressRow[] =>
    (progressQuery.data ?? []).filter((row) => row.plan_id === planId && row.day === day);

  /** Marca un día como leído (idempotente; restaura el tombstone si existía). */
  const completeDay = async (planId: string, day: number) => {
    const dbInstance = drizzle();
    const u = session.userId();
    if (!dbInstance || !u) {
      return;
    }
    const now = new Date().toISOString();
    const rows = rowsOfDay(planId, day);
    try {
      if (rows.some((row) => row.deleted_at === null)) {
        return; // ya está marcado
      }
      const tombstone = rows[0];
      if (tombstone) {
        await dbInstance
          .update(bible_reading_progress)
          .set({ deleted_at: null, updated_at: now })
          .where(eq(bible_reading_progress.id, tombstone.id));
        return;
      }
      await dbInstance.insert(bible_reading_progress).values({
        id: crypto.randomUUID(),
        user_id: u,
        plan_id: planId,
        day,
        created_at: now,
        updated_at: now,
        deleted_at: null,
      });
    } catch (e) {
      console.error("Failed to complete plan day:", e);
    }
  };

  /**
   * Desmarca un día (tombstone lógico para sincronización). Tumba TODAS las
   * filas activas de ese día (dos dispositivos pueden haberlo marcado offline).
   */
  const uncompleteDay = async (planId: string, day: number) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    const actives = (progressQuery.data ?? []).filter(
      (row) => row.plan_id === planId && row.day === day && row.deleted_at === null,
    );
    const now = new Date().toISOString();
    try {
      for (const row of actives) {
        await dbInstance
          .update(bible_reading_progress)
          .set({ deleted_at: now, updated_at: now })
          .where(eq(bible_reading_progress.id, row.id));
      }
    } catch (e) {
      console.error("Failed to uncomplete plan day:", e);
    }
  };

  const ready = () => Boolean(drizzle()) && Boolean(session.userId());

  return { activeRows, completeDay, uncompleteDay, ready };
}
